import { createContext, memo, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ZoneProps } from '../data/types'
import { alertsFromReport, type AlertsFeed } from '../zones/alerts/model'
import { useWeatherSection } from '../../app/api'

const LiveFeed = createContext<AlertsFeed | undefined>(undefined)
export const useLiveAlertFeed = () => useContext(LiveFeed)

/** Only alert readers update; the forecast generation and other zone bindings stay resident. */
export const LiveAlertsProvider = memo(function LiveAlertsProvider({ data, children }: ZoneProps & { children: ReactNode }) {
  const key = `${data.place.latitude},${data.place.longitude}`
  const reportAlerts = useWeatherSection(data.place, alertsFromReport)
  const fallback = !reportAlerts.isPlaceholderData ? reportAlerts.data : undefined
  const [live, setLive] = useState<{ key: string; feed: AlertsFeed; verified: boolean } | null>(null)
  useEffect(() => {
    if (data.mock || import.meta.env.VITE_REALTIME_ALERTS_ENABLED !== 'true') return
    let stopped = false, started = false, loading = false, unsubscribe = () => {}
    const start = () => {
      if (stopped || started || loading || document.hidden || navigator.onLine === false) return
      loading = true
      void import('../data/events').then(({ subscribeEvents }) => {
        if (!stopped) { started = true; unsubscribe = subscribeEvents({ lat: data.place.latitude, lon: data.place.longitude }, alertsFromReport(data.report), (feed, verified) => setLive({ key, feed, verified })) }
      }).catch(() => { /* A later online/visible event retries the optional chunk. */ }).finally(() => { loading = false })
    }
    start(); window.addEventListener('online', start); document.addEventListener('visibilitychange', start)
    return () => { stopped = true; unsubscribe(); window.removeEventListener('online', start); document.removeEventListener('visibilitychange', start) }
  }, [key, data.mock])
  const feed = useMemo(() => {
    const current = live?.key === key ? live.feed : undefined
    if (!current || live?.verified) return current ?? fallback
    if (!fallback && !data.report && data.pending && !current.alerts.length) return undefined
    // An initial event outage must not hide warnings in a forecast that arrives later.
    const known = fallback ?? alertsFromReport(data.report)
    const now = Date.now(), checked = Date.parse(known.freshness.as_of), previous = Date.parse(current.freshness.as_of)
    const latest = Math.max(Number.isFinite(previous) ? previous : -Infinity,
      ...current.alerts.map(alert => Date.parse(alert.issued_at ?? '')).filter(Number.isFinite))
    // A later complete official receipt supersedes unverified events, even after that receipt becomes stale/offline.
    if (known.freshness.status === 'available' && checked <= now && checked >= latest && known.sources.length > 0 &&
      known.sources.every(source => source.kind === 'official' && source.status === 'ok' && Date.parse(source.as_of) <= now)) return known
    return { ...current, alerts: [...new Map([...known.alerts, ...current.alerts].map(alert => [alert.id, alert])).values()],
      sources: current.sources.length ? current.sources : known.sources,
      freshness: { ...current.freshness, as_of: current.freshness.as_of || known.freshness.as_of, expires_at: current.freshness.expires_at || known.freshness.expires_at } }
  }, [live, key, data.report, data.pending, fallback])
  return <LiveFeed.Provider value={feed}>{children}</LiveFeed.Provider>
})
